// Avatar — uploaded picture or a coloured initial fallback. Reused everywhere.
// v2 — renders equipped cosmetics from the shop: a coloured ring (frame) and a
// crown flair, read straight off the user object so friends see them too.
// v2.1 — earned tier ring: user.tier (0–11 from the backend; -1/absent = none)
// draws a progressive ring per level tier. A purchased shop frame wins over it,
// so bought cosmetics keep their value. Milestone flairs (flair_shield/star/
// royal/bolt) are tier rewards rendered with app icons, never emoji.
import { BuzerIcon } from "../../BuzerIcons";
import { isAdminUser } from "../../lib/session";

// 12 tierov (Nováčik → Super Godlike) — farby ladia s TIER_ICON témami; vyššie tiery žiaria.
/* token-exempt-start: dekoratívna paleta avatarov — earned tier ringy, nezávislé od témy */
export const TIER_RINGS = [
  { bg: "linear-gradient(135deg,#d9cdb8,#b3a17e)" },                                            // 0 Nováčik
  { bg: "linear-gradient(135deg,#a8d08d,#63994a)" },                                            // 1 Zelenáč
  { bg: "linear-gradient(135deg,#fbbf24,#f97316)" },                                            // 2 Zapálenec
  { bg: "linear-gradient(135deg,#f0946f,#c94f2b)" },                                            // 3 Vytrvalec
  { bg: "linear-gradient(135deg,#cbd5e1,#64748b)" },                                            // 4 Stroj
  { bg: "linear-gradient(135deg,#d69e5a,#96591f)" },                                            // 5 Bojovník
  { bg: "linear-gradient(135deg,#f1f4f8,#8d99ab)", glow: "0 0 6px rgba(141,153,171,.6)" },      // 6 Majster
  { bg: "linear-gradient(135deg,#a5b4fc,#6366f1)", glow: "0 0 7px rgba(99,102,241,.55)" },      // 7 Guru
  { bg: "linear-gradient(135deg,#f7dd7f,#d4a017)", glow: "0 0 8px rgba(212,160,23,.65)" },      // 8 Šampión
  { bg: "linear-gradient(135deg,#8b5cf6,#f5d372)", glow: "0 0 9px rgba(139,92,246,.6)" },       // 9 Legenda
  { bg: "linear-gradient(135deg,#22d3ee,#a78bfa)", glow: "0 0 10px rgba(34,211,238,.65)" },     // 10 Mýtický
  { bg: "conic-gradient(from 20deg,#22d3ee,#a78bfa,#f472b6,#fbbf24,#22d3ee)",
    glow: "0 0 13px rgba(167,139,250,.9)" },                                                    // 11 Super Godlike
];
/* token-exempt-end */

// v33h — „prefarbi celý panel podľa tieru" (level-up modal aj verejný profil). Vráti štýl karty
// s gradientom tieru (stmavený, nech biely text drží kontrast) + kompletnú sadu lokálnych CSS
// premenných, ktoré preklopia CELÝ podstrom na biely-na-farbe: povrchy = priesvitná biela,
// text/ikony = biela, orámovania = priesvitná biela. Deti nemusia nič vedieť — dedia premenné.
// tier < 0 (Lite/bez levelu) → null (žiadne prefarbenie, ostáva neutrálny povrch).
/* token-exempt-start: prefarbenie panela podľa tieru — biele priesvitné vrstvy nad farbou tieru,
   nezávislé od témy (rovnaký princíp ako LevelUpModal); TIER_RINGS je jediný zdroj farieb. */
export function tierCardStyle(tier) {
  if (typeof tier !== "number" || tier < 0) return null;
  const ring = TIER_RINGS[Math.min(tier, TIER_RINGS.length - 1)];
  return {
    background: `linear-gradient(0deg, rgba(0,0,0,.32), rgba(0,0,0,.32)), ${ring.bg}`,
    ...(ring.glow ? { boxShadow: `${ring.glow}, var(--shadow-lg)` } : {}),
    "--text": "#fff",
    "--text2": "rgba(255,255,255,.82)",
    "--muted": "rgba(255,255,255,.8)",
    "--accent": "#fff",
    "--border": "rgba(255,255,255,.22)",
    "--field-border": "rgba(255,255,255,.34)",
    "--surface2": "rgba(255,255,255,.14)",
    "--surface-solid": "rgba(255,255,255,.14)",
    "--card": "rgba(255,255,255,.14)",
    "--neutral-surface": "rgba(255,255,255,.14)",
    "--neutral-surface-2": "rgba(255,255,255,.12)",
    "--neutral-surface-3": "rgba(255,255,255,.18)",
    "--neutral-border": "rgba(255,255,255,.34)",
    "--neutral-text-main": "#fff",
    "--neutral-text-muted": "rgba(255,255,255,.8)",
    "--neutral-text-subtle": "rgba(255,255,255,.66)",
    "--shadow-sm": "none",
  };
}
/* token-exempt-end */

// Item 2 — „grey tier" pre Lite profil. Lite nemá level (tier −1 → tierCardStyle vráti null), no
// majiteľka chce, aby verejný Lite profil vyzeral ROVNAKO ako farebný (hero, avatar, meno+badge,
// štatistiky, sekcie), len s TLMENÝM SIVÝM povrchom namiesto farby levelu — teda „odfarbená"
// verzia tier karty. Aplikuje sa rovnako ako tierCardStyle (na CELÝ Sheet cez cardStyle aj na obal
// verejnej stránky). Ostáva plne token-based (color-mix z --neutral-* tokenov), takže funguje v
// svetlej aj tmavej téme a text/ikony dedia normálne témové tokeny (žiadne biele-na-farbe flipy —
// tie patria len skutočným tierom). „Lite" štítok v tele ostáva viditeľný.
export function liteCardStyle() {
  return {
    // muted sivý povrch: pr-mieša neutrálnu sivú do povrchu → viditeľne sivšia karta než default Sheet
    background: "color-mix(in srgb, var(--neutral-text-subtle) 22%, var(--neutral-surface))",
    border: "1px solid var(--neutral-border)",
    boxShadow: "var(--shadow-sm)",
  };
}

// milestone flair → emblém (poloha: badge v rohu, koruna nad hlavou). Rohové badge sú
// gradientový disk s bielou ikonou a lemom vo farbe karty — pôsobí ako herný odznak.
/* token-exempt-start: dekoratívna paleta avatarov — milestone flairy, nezávislé od témy */
const MILESTONE_FLAIRS = {
  flair_shield: { icon: "shield", bg: "linear-gradient(135deg,#e3b077,#96591f)", pos: "corner", glow: "0 1px 5px rgba(150,89,31,.5)" },
  flair_star: { icon: "star2", bg: "linear-gradient(135deg,#f7dd7f,#d4a017)", pos: "corner", glow: "0 0 8px rgba(212,160,23,.75)" },
  flair_royal: { icon: "crown", color: "#d4a017", pos: "top", glow: "0 0 8px rgba(212,160,23,.85)" },
  flair_bolt: { icon: "bolt", bg: "linear-gradient(135deg,#a78bfa,#7c3aed)", pos: "corner", glow: "0 0 9px rgba(139,92,246,.8)" },
};
/* token-exempt-end */

export function Avatar({ user, size = 36, onClick }) {
  const frame = user && user.equipped_frame;   // "frame_gold" | "frame_neon"
  const flair = user && user.equipped_flair;    // "flair_crown" | "flair_halo" | milestone flair
  const tierRing = (user && typeof user.tier === "number" && user.tier >= 0)
    ? TIER_RINGS[Math.min(user.tier, TIER_RINGS.length - 1)] : null;
  /* token-exempt-start: dekoratívne shop rámiky (frame_gold/neon) + glow, nezávislé od témy */
  const shopRing = frame === "frame_gold" ? "linear-gradient(135deg,#f5d372,#d4a017)"
    : frame === "frame_neon" ? "linear-gradient(135deg,#a78bfa,#8b5cf6)" : null;
  const ring = shopRing || (tierRing && tierRing.bg);
  const ringGlow = shopRing
    ? (frame === "frame_neon" ? "0 0 8px rgba(139,92,246,.7)" : null)
    : (tierRing && tierRing.glow);
  /* token-exempt-end */
  const milestone = MILESTONE_FLAIRS[flair];
  // majiteľka/admin — bežné level/shop efekty ostávajú; navrch len pohyblivý odlesk cez fotku
  const admin = isAdminUser(user);
  const decorated = !!(ring || flair === "flair_crown" || flair === "flair_halo" || milestone);
  const base = { width: size, height: size, borderRadius: "50%", flexShrink: 0,
    objectFit: "cover", cursor: onClick ? "pointer" : "default", display: "block" };
  const title = (user && user.name) || "";
  const inner = (user && user.avatar)
    ? <img src={"/avatars/" + user.avatar} alt={title} title={title} style={base} onClick={decorated ? undefined : onClick} />
    : <div title={title} aria-label={title} onClick={decorated ? undefined : onClick} style={{ ...base,
        background: "#fff", color: "#161512" /* token-exempt: letter-avatar fallback — biely s tmavým iniciálom (oba módy) */, border: "1px solid var(--border)", display: "flex",
        alignItems: "center", justifyContent: "center", fontWeight: 800,
        fontFamily: "var(--font-display)", fontSize: Math.round(size * 0.46) }}>{((title || "?").trim().charAt(0).toUpperCase()) || "?"}</div>;
  // Admin — obal fotky s pohyblivým odleskom (orezaný na kruh); ostatné efekty ostávajú nedotknuté.
  const shown = admin
    ? <span className="bz-admin-shine-wrap" style={{ width: size, height: size, borderRadius: "50%" }}>{inner}<span className="bz-admin-shine" /></span>
    : inner;
  if (!decorated) return shown;
  const badge = Math.max(10, Math.round(size * 0.4));
  return (
    <span onClick={onClick} style={{ position: "relative", display: "inline-flex", flexShrink: 0, cursor: onClick ? "pointer" : "default",
      ...(ring ? { padding: Math.max(2, Math.round(size * 0.06)), borderRadius: "50%", background: ring, ...(ringGlow ? { boxShadow: ringGlow } : {}) } : {}) }}>
      {shown}
      {flair === "flair_crown" && <span style={{ position: "absolute", top: -Math.round(size * 0.34), left: "50%", transform: "translateX(-50%) rotate(-8deg)", fontSize: Math.round(size * 0.42), lineHeight: 1, pointerEvents: "none" }}>👑</span>}
      {flair === "flair_halo" && <span style={{ position: "absolute", top: -Math.round(size * 0.24), left: "50%", transform: "translateX(-50%)", width: Math.round(size * 0.66), height: Math.round(size * 0.22), borderRadius: "50%", border: `${Math.max(2, Math.round(size * 0.055))}px solid #f5d372`, boxShadow: "0 0 9px 2px rgba(245,211,114,.85)" /* token-exempt: dekoratívne halo (flair) */, pointerEvents: "none" }} />}
      {milestone && milestone.pos === "top" && (
        <span style={{ position: "absolute", top: -Math.round(size * 0.3), left: "50%", transform: "translateX(-50%)", lineHeight: 0, pointerEvents: "none", filter: milestone.glow ? `drop-shadow(${milestone.glow})` : undefined }}>
          <BuzerIcon name={milestone.icon} size={Math.round(size * 0.42)} color={milestone.color} />
        </span>
      )}
      {/* corner badge dole vpravo — kamera „Zmeniť fotku" na Mojom profile je dole vľavo */}
      {milestone && milestone.pos === "corner" && (
        <span style={{ position: "absolute", right: -Math.round(badge * 0.22), bottom: -Math.round(badge * 0.18), width: badge, height: badge,
          borderRadius: "50%", background: milestone.bg, border: `${Math.max(1.5, Math.round(size * 0.028))}px solid var(--surface)`,
          display: "inline-flex", alignItems: "center", justifyContent: "center", pointerEvents: "none",
          boxShadow: milestone.glow || "var(--elev-1)" }}>
          <BuzerIcon name={milestone.icon} size={Math.round(badge * 0.62)} color="var(--on-solid)" />
        </span>
      )}
    </span>
  );
}
